import React from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useHistory } from 'react-router-dom';
import {
  Card,
  Col,
  Container,
  Label,
  Row,
} from 'reactstrap';

import Meta from 'components/App/Meta';
import Button from 'components/App/Button';
import Loading from 'components/App/Loading';
import Pagination from 'components/Pagination';
import Breadcrumb from 'components/App/Breadcrumb';
import AppInputValidation from 'components/App/InputValidation';
import AppSelectValidation from 'components/App/SelectValidation';

import SearchIcon from 'assets/images/search-icon.svg';
import { LOCATIONS, themeColor } from 'constants/index';
import cn from 'utils/classnames';
import { getParamsFormObject, getPlaceIdFromReview } from 'utils';
import ManagerReviewsDataTableList from './ManagerReviewsDataTableList';
import styles from './ManagerReviews.module.scss';

const PAGE_TITLE = 'Quản lý đánh giá';

const breadcrumbs = [
  {
    name: PAGE_TITLE,
    isActive: true,
    link: LOCATIONS.MANAGER_REVIEWS,
  },
];

const DEFAULT_LIMIT = 20;

const RATING_FILTER_OPTIONS = [
  { label: 'Tất cả', value: '' },
  { label: 'Xuất sắc', value: 'EXCELLENT' },
  { label: 'Tốt', value: 'GOOD' },
  { label: 'Ổn', value: 'OKAY' },
  { label: 'Tệ', value: 'POOR' },
  { label: 'Rất tệ', value: 'VERY_POOR' },
];

function ManagerReviews(props) {
  const {
    managerReviews,
    totalManagerReviews,
    isLoadingGetManagerReviews,
    actionGetManagerReviews,
  } = props;

  const history = useHistory();

  const urlParams = React.useMemo(
    () => new URLSearchParams(history.location.search),
    [history.location.search],
  );

  const searchQueries = React.useMemo(() => ({
    page: Number(urlParams.get('page') || 1),
    q: urlParams.get('q') || '',
    rate: urlParams.get('rate') || '',
  }), [urlParams]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      q: searchQueries.q,
      rate: searchQueries.rate,
    },
    onSubmit: (values) => {
      history.push({
        search: getParamsFormObject({
          q: values.q,
          rate: values.rate || '',
          page: 1,
        }),
      });
    },
  });

  const onResetForm = React.useCallback(() => {
    validation.resetForm({
      values: {
        q: '',
        rate: '',
      },
    });

    history.push({
      search: getParamsFormObject({
        q: '',
        rate: '',
        page: 1,
      }),
    });
  }, [history, validation]);

  const onChangeFilterOption = React.useCallback((e) => {
    validation.setFieldValue(e.name, e.value, true);
  }, [validation]);

  const onChangePage = React.useCallback((selected) => {
    history.push({
      search: getParamsFormObject({
        ...searchQueries,
        page: selected.selected + 1,
      }),
    });
  }, [history, searchQueries]);

  const onOpenDetail = React.useCallback((reviewItem) => {
    history.push({
      pathname: LOCATIONS.MANAGER_REVIEWS_DETAIL,
      search: getParamsFormObject({
        place: getPlaceIdFromReview(reviewItem),
        review: reviewItem?._id || '',
      }),
    });
  }, [history]);

  const pageCount = React.useMemo(() => (
    Math.ceil((totalManagerReviews || 0) / DEFAULT_LIMIT)
  ), [totalManagerReviews]);

  React.useEffect(() => {
    actionGetManagerReviews({
      page: searchQueries.page,
      limit: DEFAULT_LIMIT,
      q: searchQueries.q || undefined,
      rate: searchQueries.rate || undefined,
    });
  }, [
    actionGetManagerReviews,
    searchQueries.page,
    searchQueries.q,
    searchQueries.rate,
  ]);

  return (
    <>
      <Meta name={PAGE_TITLE} />

      <div className={`page-content ${styles.Page}`}>
        <Container fluid>
          <Breadcrumb pageTitle={PAGE_TITLE} breadcrumbList={breadcrumbs} />

          <Loading loading={isLoadingGetManagerReviews} loaderOnPage />

          <Card className="Section">
            <form onSubmit={validation.handleSubmit}>
              <Row className="align-items-end">
                <Col xxl="6" className="mb-24">
                  <Label className="Section__Search__Label">Tìm kiếm</Label>

                  <div className="Section__Search__Container">
                    <AppInputValidation
                      id="q"
                      name="q"
                      type="text"
                      validation={validation}
                      inputClassName={cn('Section__Search__Input')}
                      placeholder="Tìm theo Mã khách hàng, Mã đặt phòng, Mã chỗ nghỉ..."
                    />

                    <img
                      className="fas fa-search Section__Search__Icon__Search"
                      src={SearchIcon}
                      alt="search"
                    />
                  </div>
                </Col>

                <Col xxl="3" md="4" className="mb-24">
                  <Label className="Section__Search__Label">Lọc theo đánh giá</Label>

                  <AppSelectValidation
                    name="rate"
                    isHiddenSearchable
                    validation={validation}
                    placeholder="Tất cả"
                    options={RATING_FILTER_OPTIONS}
                    value={validation.values.rate}
                    onChange={onChangeFilterOption}
                    selectedClassName="Section__Search__Select"
                  />
                </Col>

                <Col xxl="auto" className="mb-24">
                  <div className="d-flex">
                    <Button
                      outline
                      classes="Section__Search__Button__ReType mr-24"
                      color={themeColor.LIGHT}
                      onClick={onResetForm}
                    >
                      Nhập lại
                    </Button>

                    <Button
                      classes="Section__Search__Button__Search"
                      type="submit"
                    >
                      Tìm kiếm
                    </Button>
                  </div>
                </Col>
              </Row>
            </form>
          </Card>

          <Card className={cn('Section', styles.TableCard)}>
            <div className="Table__Body">
              <p className="Table__Body__Total">
                {`${totalManagerReviews} đánh giá`}
              </p>

              <ManagerReviewsDataTableList
                list={managerReviews}
                onOpenDetail={onOpenDetail}
              />
            </div>

          </Card>
        </Container>
        {pageCount > 1 && (
          <Pagination
            page={searchQueries.page}
            pageCount={pageCount}
            onChangePage={onChangePage}
          />
        )}
      </div>
    </>
  );
}

ManagerReviews.propTypes = {
  managerReviews: PropTypes.instanceOf(Array),
  totalManagerReviews: PropTypes.number,
  isLoadingGetManagerReviews: PropTypes.bool.isRequired,
  actionGetManagerReviews: PropTypes.func.isRequired,
};

ManagerReviews.defaultProps = {
  managerReviews: [],
  totalManagerReviews: 0,
};

export default ManagerReviews;
